import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useBetterAuth } from '../../hooks/useAuth';
import { AuthLoadingFallback } from './AuthLoadingFallback';

/**
 * Route-level guard that blocks rendering until the authenticated user's
 * permission set has loaded, then gates on one or more permissions.
 *
 * The `BetterAuthContext` awaits `fetchPermissions` before flipping `loading`
 * to `false`, so by the time we render here `user.permissions` is normally a
 * (possibly empty) array. A transient `undefined` can still appear during
 * sign-in / refresh flows — render a lightweight loading placeholder in that
 * case instead of returning `null` (which leaves the user staring at a blank
 * page forever if the permission fetch silently failed).
 *
 * Two prop forms (exactly one required, mutually exclusive at the type
 * level):
 *
 *   - `permission="orders:read"` — single-permission gate. Canonical
 *     syntax for the 30+ single-perm routes in `App.tsx`. Stays
 *     preferred for readability when only one perm is needed.
 *
 *   - `permissions={['inventory:read', 'catalog:read']}` — AND
 *     gate; user must hold **every** entry. Use when the page fires
 *     multiple backend calls each requiring a distinct permission
 *     (e.g. StockLevelsPage hits `/admin/inventory/*` requiring
 *     `INVENTORY_READ` AND `/catalog/*` requiring
 *     `CATALOG_READ`). Without AND gating, a user holding only
 *     the route's nominal permission would pass the route gate,
 *     navigate to the page, then watch every catalog request return
 *     403 — the UI has no actionable error path for partial grants.
 *     (#465 + ADR-0005)
 *
 *   - `anyPermissions={['a:read', 'b:read']}` — OR gate; user must hold
 *     **at least one** entry. Use when a route hosts several
 *     independently-gated surfaces and holding any one of them justifies
 *     reaching the page, which then block-gates each surface internally.
 *     No route in `App.tsx` currently uses this form — the dispatch route
 *     was the last one, until #2248 narrowed it back to a single
 *     `dispatch:read` gate once the `webhook_failures:read`-only surface
 *     it used to admit (a monitoring tab) was retired. The form stays
 *     supported for the next route that genuinely needs an OR of keys.
 *
 * Both forms exit through the same `/unauthorized` redirect when the
 * check fails — operators don't need to distinguish "missing one of
 * many" from "missing one specific" perm in the UI.
 *
 * The single-`permission` form is semantically the degenerate
 * one-element case of `permissions`; both forms share one runtime
 * code path (`required.every(p => perms.includes(p))`). This unifies
 * the two surfaces into one path to test + one path to reason about.
 *
 * `permissions` is typed as a non-empty tuple
 * `readonly [string, ...string[]]` so `permissions={[]}` is a
 * **compile error** rather than a silent always-pass (an empty
 * `.every()` returns `true` — EQD §3 silent-fallback would be a
 * real bug; the type system is the cleanest preventer per EQD §7
 * surface-area discipline).
 *
 * **Call-site note**: pass an array **literal**. A variable typed
 * as `string[]` (or `readonly string[]`) won't satisfy the
 * non-empty tuple — widen via `as readonly [string, ...string[]]`
 * at the call site if you must compute the list dynamically.
 * Route declarations in `App.tsx` always use inline literals so
 * this is a non-issue today.
 */
type PermissionGuardProps = {
  children: React.ReactNode;
} & (
  | { permission: string; permissions?: never; anyPermissions?: never }
  | { permissions: readonly [string, ...string[]]; permission?: never; anyPermissions?: never }
  | { anyPermissions: readonly [string, ...string[]]; permission?: never; permissions?: never }
);

export const PermissionGuard: React.FC<PermissionGuardProps> = (props) => {
  const { children } = props;
  const { user, isAuthenticated, loading } = useBetterAuth();
  const location = useLocation();

  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;

  const perms = user?.permissions;
  if (perms === undefined) {
    return <AuthLoadingFallback variant="inline" />;
  }

  // OR gate: holding any one listed permission is sufficient. Kept as a
  // distinct branch (not folded into the `.every()` path) because the
  // semantics genuinely differ — `.some()` vs `.every()`.
  if (props.anyPermissions) {
    if (!props.anyPermissions.some((p) => perms.includes(p))) {
      return <Navigate to="/unauthorized" replace />;
    }
    return <>{children}</>;
  }

  // Single-form callers contribute a one-element list; array-form
  // callers contribute their full set. Both flow through the same
  // `.every()` AND check — single-perm semantics is the degenerate
  // one-element case of the AND gate, so there's one code path
  // through this component (not two) and one path to test.
  const required: readonly string[] = props.permissions ?? [props.permission!];

  if (!required.every((p) => perms.includes(p))) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
};
